import React, { ChangeEventHandler, Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button } from '@mui/material';

import { extractUploadedFilesFromZip, FileFromBrowserUpload, UploadedFile } from '@paragon/component-processor';

import { batchFiles } from '../../actions/batchFiles';
import fileTypes from '../../data/fileTypes';

const fileTypesAcceptedForUpload = [...fileTypes.mitek.fileExtensions, ...fileTypes.simpson.fileExtensions, 'zip'];
const fileTypesToAcceptForUploadHtmlAccept = fileTypesAcceptedForUpload.map((fileType) => `.${fileType}`).join(',');
function getFilesOfTypesAcceptedForUpload(files: readonly UploadedFile[]) {
  return files.filter((file) =>
    fileTypesAcceptedForUpload.map((fileType) => fileType.toLowerCase()).includes(file.filetype!.toLowerCase()),
  );
}

function resetEventTargetValue(event: React.MouseEvent<HTMLInputElement>) {
  (event.target as any).value = null;
}

type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = DispatchProps;

interface State {
  readonly fileData: readonly UploadedFile[] | null;
  readonly inputErrorText: string | null;
  readonly numberOfFilesBeingBatched?: number;
}

class BatchControls extends Component<Props, State> {
  state: State = {
    fileData: null,
    inputErrorText: null,
  };

  onSetFile: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const parsedFiles = [...event.target.files!].map((file) => new FileFromBrowserUpload(file));
    const filesOfFileTypesAcceptedForUpload = getFilesOfTypesAcceptedForUpload(parsedFiles);

    const zipFiles = filesOfFileTypesAcceptedForUpload.filter((file) => file.filetype!.toLowerCase() === 'zip');
    let numberOfFilesBeingBatched = filesOfFileTypesAcceptedForUpload.length - zipFiles.length;
    await Promise.all(
      zipFiles.map(async (zipFile) => {
        await zipFile.getContents();
        const filesInZipFile = await extractUploadedFilesFromZip(zipFile.contents!);
        const filesInZipFileAcceptedForUpload = getFilesOfTypesAcceptedForUpload(filesInZipFile);
        numberOfFilesBeingBatched += filesInZipFileAcceptedForUpload.length;
      }),
    );

    this.setState({
      fileData: filesOfFileTypesAcceptedForUpload,
      numberOfFilesBeingBatched,
    });
  };

  onBatch = () => {
    const inputErrorText = [];
    if (this.state.fileData == null) {
      inputErrorText.push('Select file(s) to batch.');
    }
    if (inputErrorText.length > 0) {
      this.setState({ inputErrorText: inputErrorText.join(' ') });
      return;
    }

    this.props.batchFiles(this.state.fileData!);

    this.setState({
      inputErrorText: null,
      fileData: null,
    });
  };

  render() {
    let selectFilesButtonText = 'Select Files';
    if (this.state.fileData != null) {
      if (this.state.numberOfFilesBeingBatched === 1) selectFilesButtonText = '1 file';
      else selectFilesButtonText = `${this.state.numberOfFilesBeingBatched} files`;
    }

    return (
      <>
        <Box>{this.state.inputErrorText}</Box>
        <Box sx={{ display: 'flex', my: 2 }}>
          <Box sx={{ flex: 1, mx: 5 }}>
            <input
              id="upload-input"
              type="file"
              accept={fileTypesToAcceptForUploadHtmlAccept}
              multiple
              onChange={this.onSetFile}
              onClick={resetEventTargetValue}
              hidden
            />
            <label htmlFor="upload-input">
              <Button variant="outlined" fullWidth component="span">
                {selectFilesButtonText}
              </Button>
            </label>
          </Box>
          <Box sx={{ flex: 1, mx: 5 }}>
            <Button variant="outlined" fullWidth onClick={this.onBatch}>
              Batch
            </Button>
          </Box>
        </Box>
      </>
    );
  }
}

const actionCreators = {
  batchFiles,
};

export default connect(null, actionCreators)(BatchControls);
